import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import toast from 'react-hot-toast';
import { MediaProviders } from 'constants/media';
import {
  useUpdateUserCoverImageMutation,
  useUpdateUserProfileImageMutation,
} from 'types/generated/client';
import { useImageCropper } from 'hooks/useImageCropper';
import { useUploadSignedFile } from 'hooks/useUploadSignedFile';
import { useViewer } from 'hooks/useViewer';
import type { ImageCropperOptions } from 'components/ImageCropper';

type UploadTarget = 'cover' | 'avatar';

// Square avatar (it renders square in places, so the mask stays honest) and a ~3:1 cover strip.
const CROP_OPTIONS: Record<UploadTarget, ImageCropperOptions> = {
  cover: { aspectRatio: 3, title: 'Crop cover photo' },
  avatar: { aspectRatio: 1, title: 'Crop profile photo' },
};

/**
 * In-place cover/avatar editing for the viewer's own profile: pick a file,
 * upload it (Cloudflare signed upload), persist the users.*_image_* columns and
 * refetch. An object-URL preview shows immediately while the upload runs.
 */
export function useProfileImageUploads(refetch: () => void) {
  const viewer = useViewer();
  const { uploadSignedFile } = useUploadSignedFile();
  const { cropImage, CropperModal } = useImageCropper();
  const [updateCoverImage] = useUpdateUserCoverImageMutation();
  const [updateProfileImage] = useUpdateUserProfileImageMutation();

  const coverInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [uploadingTarget, setUploadingTarget] = useState<UploadTarget | null>(null);
  const [previews, setPreviews] = useState<Record<UploadTarget, string | null>>({
    cover: null,
    avatar: null,
  });

  // Object URLs outlive the component otherwise; the previews are per-session.
  useEffect(
    () => () =>
      setPreviews((current) => {
        Object.values(current).forEach((url) => url && URL.revokeObjectURL(url));
        return current;
      }),
    [],
  );

  const clearPreview = (target: UploadTarget) => {
    setPreviews((current) => {
      if (current[target]) URL.revokeObjectURL(current[target] as string);
      return { ...current, [target]: null };
    });
  };

  const handleFileSelected = async (target: UploadTarget, picked: File | undefined) => {
    if (!picked || !viewer.userId || uploadingTarget) return;

    const file = await cropImage(picked, CROP_OPTIONS[target]);
    if (!file) return;

    setUploadingTarget(target);
    setPreviews((current) => ({ ...current, [target]: URL.createObjectURL(file) }));

    try {
      const response = await uploadSignedFile({ file });

      if (response) {
        const providerId = response.fileName.split('.')[0];

        if (target === 'cover') {
          await updateCoverImage({
            variables: {
              id: viewer.userId,
              coverImageFileName: response.fileName,
              coverImagePath: response.path,
              coverImageProvider: MediaProviders.Cloudflare,
              coverImageProviderId: providerId,
              coverImageProviderUrl: response.providerUrl,
              coverImageUrl: response.url,
            },
          });
        } else {
          await updateProfileImage({
            variables: {
              id: viewer.userId,
              profileImageFileName: response.fileName,
              profileImagePath: response.path,
              profileImageProvider: MediaProviders.Cloudflare,
              profileImageProviderId: providerId,
              profileImageProviderUrl: response.providerUrl,
              profileImageUrl: response.url,
            },
          });
        }

        refetch();
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Photo upload failed, please try again');
      clearPreview(target);
    } finally {
      setUploadingTarget(null);
    }
  };

  // The preview outlives the upload on purpose: it bridges the gap until the
  // refetched profile carries the new path, and the next pick replaces it.
  return {
    CropperModal,
    coverInputRef,
    avatarInputRef,
    coverPreviewUrl: previews.cover,
    avatarPreviewUrl: previews.avatar,
    isUploadingCover: uploadingTarget === 'cover',
    isUploadingAvatar: uploadingTarget === 'avatar',
    openCoverPicker: () => coverInputRef.current?.click(),
    openAvatarPicker: () => avatarInputRef.current?.click(),
    handleCoverFile: (file: File | undefined) => void handleFileSelected('cover', file),
    handleAvatarFile: (file: File | undefined) => void handleFileSelected('avatar', file),
  };
}
